'use client';
import { useSuspenseQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { RefObject, Suspense, useEffect, useRef, useState } from 'react';
import { When } from 'react-if';

import cn from '@/lib/clsxm';
import { useClientExtraLargeScreen, useClientSmallScreen } from '@/lib/user-agent/client';

import Container from '@/components/@base/container';
import TabGroup, { Tab, TabPanels, Tabs } from '@/components/@base/tab';
import Panel from '@/components/@base/tab/panel';
import { MARKETS, PRODUCT_TABS } from '@/components/app/product/components/@shared/type';
import BundleComments from '@/components/app/product/components/bundle-comments';
import Comments from '@/components/app/product/components/comments';
import Content from '@/components/app/product/components/content';
import Questions from '@/components/app/product/components/questions';
import BundleQuestions from '@/components/app/product/components/questions/bundle-question';
import SideBar from '@/components/app/product/components/side-bar';
import Support from '@/components/app/product/components/support';
import TableContents from '@/components/app/product/components/table-contents';
import { useTableContentItems } from '@/components/app/product/components/table-contents/constant';
import ShareButtons from '@/components/app/product/components/tabs/share-buttons';
import Props from '@/components/app/product/components/tabs/type';
import DeveloperDiscountCodes from '@/components/shared/components/developer-discount-codes';
import IconTextCombo from '@/components/shared/components/icon-text-combo';
import { getStoreDiscountOptions } from '@/components/shared/services/hooks';

import {
  DATA_CY_PRODUCT_COMMENTS_TAB,
  DATA_CY_PRODUCT_DESCRIBE_TAB,
  DATA_CY_PRODUCT_QUESTIONS_TAB,
  DATA_CY_PRODUCT_SUPPORT_TAB,
} from '@/constant/cypress';
import { pageLevelLocalization } from '@/constant/localization';

import ContentIcon from '~/images/icons/content-icon';
import DeveloperDiscountCodesIcon from '~/images/icons/developer-discount-codes';
import HeadphoneIcon from '~/images/icons/headphone-icon';
import MessageQuestionIcon from '~/images/icons/messageQuestionIcon';
import MessageStarIcon from '~/images/icons/messageStarIcon';

const StickyTableWrapper = dynamic(
  () => import('@/components/app/product/components/table-contents/sticky-table-wrapper'),
  {
    ssr: false,
  },
);

const { product: productLocalization } = pageLevelLocalization;

export default function ProductTabs({
  slug,
  rate,
  productId,
  initialTab = PRODUCT_TABS.CONTENT,
  market = MARKETS.PRODUCT,
  isSubscription,
  store,
  tableOfContents,
  reviews,
  hasQuestions,
  isReadySite,
}: Props) {
  const { data: storeDiscount } = useSuspenseQuery(getStoreDiscountOptions(store));
  const isExtraLarge = useClientExtraLargeScreen();
  const isSmallScreen = useClientSmallScreen();
  const tabs = Object.values(PRODUCT_TABS);
  const [isSidebarSticky, setIsSidebarSticky] = useState(true);
  const [currentTab, setCurrentTab] = useState<PRODUCT_TABS>(initialTab);
  const router = useRouter();
  const path = usePathname();
  const tabsPanelTopRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;
  const questionBoxRef = useRef<HTMLDivElement>(null);

  const tabClassName =
    'transition duration-300 px-3 md:px-15px pt-12 md:pt-[22px] focus:outline-hidden font-medium text-[#76767C] text-sm pb-4 ui-selected:border-b-2 ui-selected:border-[#FF9606] ui-selected:font-bold ui-selected:text-[#565656] hover:text-[#565656]';

  const handleTabChange = (index) => {
    const availableTabs = reviews ? tabs : tabs.filter((tab) => tab !== PRODUCT_TABS.COMMENTS);
    const selectedTab = availableTabs[index];
    setCurrentTab(selectedTab);

    // Don't change URL if this tab change is triggered by the tour
    // to prevent page reload and tour reset
    if ((window as any).__tourTabClick) {
      return;
    }

    if (selectedTab === PRODUCT_TABS.CONTENT) {
      router.push(`/web/${slug}`, { scroll: false });
    } else {
      router.push(`/web/${slug}/${selectedTab}`, { scroll: false });
    }
  };

  useEffect(() => {
    if (
      path.includes(PRODUCT_TABS.SUPPORT) ||
      (path.includes(PRODUCT_TABS.COMMENTS) && market === MARKETS.BUNDLE)
    ) {
      setIsSidebarSticky(false);
    }
  }, [path]);

  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  const [activeTableItem, setActiveTableItem] = useState<string>('productSpecifications');
  const [isManuallyScrolling, setIsManuallyScrolling] = useState<boolean>(false);
  const [shouldScrollToQuestionBox, setShouldScrollToQuestionBox] = useState(false);
  const [showTableContents, setShowTableContents] = useState<boolean>(false);

  const handleTableItemClick = (tabKey: PRODUCT_TABS, sectionId: string, itemKey: string) => {
    setActiveTableItem(itemKey || 'productSpecifications');
    setScrollTarget(sectionId || null);
    setIsManuallyScrolling(true);

    // Reset manual scrolling flag after scroll animation completes
    setTimeout(() => {
      setIsManuallyScrolling(false);
    }, 1000); // Slightly longer than scroll animation + highlight duration

    // Create a filtered tabs array for this function
    const availableTabs = reviews ? tabs : tabs.filter((tab) => tab !== PRODUCT_TABS.COMMENTS);
    const index = availableTabs.indexOf(tabKey);
    if (index !== -1) {
      handleTabChange(index);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined' || scrollTarget === null) return;

    const timer = setTimeout(() => {
      const el = scrollTarget
        ? document.getElementById(scrollTarget)
        : document.querySelector(`#tab-panel-${currentTab}`);

      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 100; // offset
        window.scrollTo({ top: y, behavior: 'smooth' });

        el.classList.add('highlight-section');
        setTimeout(() => {
          el.classList.remove('highlight-section');
        }, 1000);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [currentTab, scrollTarget]);
  const items = useTableContentItems({
    hasReviews: reviews && reviews?.length > 0,
    hasQuestions,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Don't update active item if user manually scrolled
        if (isManuallyScrolling) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const id = visible[0].target.id;
          const matchedItem = items.find((i) => i.sectionId === id && i.tab === currentTab);
          if (matchedItem) {
            setActiveTableItem(matchedItem.key);
          }
        } else if (visible.length === 0 && activeTableItem === 'productDescriptionSection') {
          // When no sections are visible, default to productSpecifications as the first item
          setActiveTableItem('productSpecifications');
        }
      },
      {
        root: null,
        rootMargin: '-120px 0px -70% 0px',
        threshold: 0.1,
      },
    );

    // Wait for DOM elements to be ready
    const setupObserver = () => {
      const elementsToObserve = items
        .filter((i) => i.tab === currentTab && i.sectionId)
        .filter((item) => document.getElementById(item.sectionId)); // Only observe elements that exist in DOM

      elementsToObserve.forEach((item) => {
        const el = document.getElementById(item.sectionId);
        if (el) {
          observer.observe(el);
        }
      });
    };

    // Small delay to ensure DOM elements are ready
    const timer = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [currentTab, isManuallyScrolling]);

  useEffect(() => {
    if (currentTab === PRODUCT_TABS.COMMENTS) {
      setActiveTableItem('commentsSection');
    } else if (currentTab === PRODUCT_TABS.QUESTIONS) {
      setActiveTableItem('questionsSection');
    } else if (currentTab !== PRODUCT_TABS.CONTENT) {
      setActiveTableItem('productSpecifications');
    }
  }, [currentTab]);

  useEffect(() => {
    if (!shouldScrollToQuestionBox) return;
    let attempts = 0;
    const maxAttempts = 30;

    const checkAndScroll = () => {
      attempts++;
      const element = questionBoxRef?.current || document.getElementById('question-box');

      const loadingElements = document.querySelectorAll('[data-loading="question-loading"]');
      const isDataLoaded = loadingElements.length === 0;

      if (element && isDataLoaded) {
        setTimeout(() => {
          const y =
            element.getBoundingClientRect().top + window.scrollY - (isSmallScreen ? 150 : 70);
          window.scrollTo({ top: y, behavior: 'smooth' });
          setShouldScrollToQuestionBox(false);
        }, 200);
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkAndScroll, 100);
      } else {
        setShouldScrollToQuestionBox(false);
      }
    };

    const timer = setTimeout(checkAndScroll, 200);

    return () => clearTimeout(timer);
  }, [shouldScrollToQuestionBox, currentTab, isSmallScreen]);
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (isExtraLarge !== undefined && isExtraLarge) return;
    if (!tableOfContents) return;

    const handleScroll = () => {
      hasScrolled.current = true;

      const scrollY = window.scrollY || window.pageYOffset;
      setShowTableContents(scrollY >= 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isExtraLarge, tableOfContents]);

  const handleAddCommentClick = () => {
    setCurrentTab(PRODUCT_TABS.QUESTIONS);
    handleTabChange(2);
    setShouldScrollToQuestionBox(true);
  };

  // Create a filtered tabs array for display
  const availableTabs = reviews ? tabs : tabs.filter((tab) => tab !== PRODUCT_TABS.COMMENTS);

  return (
    <TabGroup selectedIndex={availableTabs.indexOf(currentTab)} onChange={handleTabChange}>
      <Container className='relative w-full bg-[#FFFFFF] md:border-t-[1px] md:border-t-[#EDEEF2]'>
        {isExtraLarge !== undefined && !isExtraLarge && tableOfContents && (
          <Container
            className={cn(
              'fixed top-[540px] z-50 transition-all duration-500 ease-in-out md:top-96',
              showTableContents
                ? 'pointer-events-auto right-[-16px] opacity-100'
                : 'pointer-events-none right-[-200px] opacity-0',
            )}
          >
            <TableContents
              activeItem={activeTableItem}
              onSelectTab={(tab, sectionId, key) => handleTableItemClick(tab, sectionId, key)}
              hasReviews={reviews && reviews?.length > 0}
              hasQuestions={hasQuestions}
            />
          </Container>
        )}
        <Tabs className='relative mx-auto flex max-w-[1279px] columns-3 overflow-x-auto px-4 md:col-span-3 md:px-0'>
          <Tab
            className={tabClassName}
            id='productDescription'
            data-cy={DATA_CY_PRODUCT_DESCRIBE_TAB}
          >
            {({ selected, hover }) => (
              <IconTextCombo
                Icon={
                  <ContentIcon width='20' color={selected || hover ? '#FFA22B' : '#76767C'} />
                }
                title={productLocalization.content}
                wrapperClassName='p-0  whitespace-nowrap'
              />
            )}
          </Tab>
          <When condition={!!reviews}>
            <Tab
              data-cy={DATA_CY_PRODUCT_COMMENTS_TAB}
              id='commentsTitle'
              className={tabClassName}
            >
              {({ selected, hover }) => (
                <IconTextCombo
                  Icon={
                    <MessageStarIcon
                      width='20'
                      color={selected || hover ? '#FFA22B' : '#76767C'}
                    />
                  }
                  title={productLocalization.comments}
                  wrapperClassName='p-0'
                />
              )}
            </Tab>
          </When>
          <Tab
            data-cy={DATA_CY_PRODUCT_QUESTIONS_TAB}
            id='questionsTitle'
            className={tabClassName}
          >
            {({ selected, hover }) => (
              <IconTextCombo
                Icon={
                  <MessageQuestionIcon
                    width='20'
                    color={selected || hover ? '#FFA22B' : '#76767C'}
                  />
                }
                title={productLocalization.questions}
                wrapperClassName='p-0'
              />
            )}
          </Tab>
          <Tab data-cy={DATA_CY_PRODUCT_SUPPORT_TAB} id='support' className={tabClassName}>
            {({ selected, hover }) => (
              <IconTextCombo
                Icon={
                  <HeadphoneIcon
                    width='20'
                    color={selected || hover ? '#FFA22B' : '#76767C'}
                  />
                }
                title={productLocalization.support}
                wrapperClassName='p-0'
              />
            )}
          </Tab>
          <When condition={storeDiscount?.length > 0}>
            <Tab id='developerDiscountCodes' className={tabClassName}>
              {({ selected, hover }) => (
                <IconTextCombo
                  Icon={
                    <DeveloperDiscountCodesIcon
                      width='20'
                      color={selected || hover ? '#FFA22B' : '#76767C'}
                    />
                  }
                  title={productLocalization.developerDiscountCodes}
                  wrapperClassName='p-0  whitespace-nowrap'
                />
              )}
            </Tab>
          </When>
          <ShareButtons slug={slug} productId={productId} />
        </Tabs>
        <Container className='relative flex w-full bg-[#F9FAFC]'>
          <Container
            ref={tabsPanelTopRef}
            className={cn(
              'relative mx-auto max-w-[1279px] md:flex md:justify-between md:gap-6 md:pt-2',
            )}
          >
            {isExtraLarge !== undefined && isExtraLarge && tableOfContents && (
              <StickyTableWrapper>
                <TableContents
                  activeItem={activeTableItem}
                  onSelectTab={(tab, sectionId, key) =>
                    handleTableItemClick(tab, sectionId, key)
                  }
                  hasReviews={reviews && reviews?.length > 0}
                  hasQuestions={hasQuestions}
                />
              </StickyTableWrapper>
            )}
            <TabPanels className='w-full max-w-full min-w-full py-2 md:max-w-[900px] md:min-w-[900px] md:flex-1 md:p-0'>
              <Panel
                key='content'
                className='max-w-[384px]! min-w-[384px] md:max-w-full! md:min-w-full!'
              >
                <Content
                  slug={slug}
                  tabsPanelTopRef={tabsPanelTopRef}
                  questionBoxRef={questionBoxRef}
                  onTabChange={setCurrentTab}
                  onScrollToQuestionBox={() => setShouldScrollToQuestionBox(true)}
                  isReadySite={isReadySite}
                />
              </Panel>
              <When condition={!!reviews}>
                <Panel
                  key='comments'
                  className={cn(
                    market === MARKETS.BUNDLE &&
                      'sticky top-[100px] mb-4 max-w-[384px]! min-w-[384px] md:max-w-full! md:min-w-full!',
                  )}
                >
                  {market === MARKETS.BUNDLE ? (
                    <BundleComments slug={slug} />
                  ) : (
                    <Suspense>
                      <Comments
                        handleAddCommentClick={handleAddCommentClick}
                        slug={slug}
                        rate={rate}
                        productId={productId}
                      />
                    </Suspense>
                  )}
                </Panel>
              </When>
              <Panel
                className='max-w-[384px]! min-w-[384px] md:max-w-full! md:min-w-full!'
                key='questions'
              >
                {market === MARKETS.BUNDLE ? (
                  <BundleQuestions slug={slug} />
                ) : (
                  <Suspense>
                    <Questions
                      questionBoxRef={questionBoxRef}
                      slug={slug}
                      rate={rate}
                      productId={productId}
                    />
                  </Suspense>
                )}
              </Panel>
              <Panel
                key='support'
                className='sticky top-[100px] mb-4 max-w-[384px]! min-w-[384px] md:max-w-full! md:min-w-full!'
              >
                <Support slug={slug} productId={productId} isSubscription={isSubscription} />
              </Panel>
              <When condition={storeDiscount?.length > 0}>
                <Panel
                  key='developerDiscount'
                  className='sticky top-[100px] mt-5 mb-4 max-w-[384px]! min-w-[384px] md:max-w-full! md:min-w-full!'
                >
                  <DeveloperDiscountCodes products={storeDiscount} />
                </Panel>
              </When>
            </TabPanels>
            <SideBar
              slug={slug}
              productId={productId}
              className='hidden w-full max-w-[350px] md:block'
              stickySideBar={isSidebarSticky}
            />
          </Container>
        </Container>
      </Container>
    </TabGroup>
  );
}
